import React, { useState } from 'react';
import { CreateClientDrawer } from './create-drawer/CreateClientDrawer';
import { useClientStore } from '../../lib/stores/clientStore';
import { useApp } from '../AppProvider';
import { useSettingsStore } from '../../lib/stores/settingsStore';
import { translations } from '../../lib/i18n/translations';
import { useMenuLabels } from '../../lib/hooks/useMenuLabels';

interface CreateClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialName?: string;
  initialData?: any;
  onClientCreated: (clientId: string) => void;
  /** Nuova anagrafica come Lead (stato e pipeline di default dei lead), es. dalle chat. */
  isLead?: boolean;
}

export function CreateClientModal({ isOpen, onClose, initialName, initialData, onClientCreated, isLead = false }: CreateClientModalProps) {
  const { createClient, updateClient } = useClientStore();
  const { toast } = useApp();
  const { settings } = useSettingsStore();
  const { client: clientLabel } = useMenuLabels();
  const t = translations[settings?.language || 'en'].clients;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditing = Boolean(initialData?.id);

  // Modello Contatti/Aziende first-class: se stai editando un referente (persona senza
  // lifecycle, collegata a un'azienda via client_relationships) NON va promosso a cliente.
  // Il fallback hardcoded 'cliente' qui sotto lo promuoverebbe: preserva stato/pipeline NULL.
  const isReferenteEdit = isEditing
    && initialData?.entity_type === 'individual'
    && !initialData?.stato_cliente
    && !initialData?.pipeline_id;

  const handleSubmit = async (formData: any) => {
    setIsSubmitting(true);
    try {
      let result;
      if (isEditing) {
        // Update existing client/lead
        result = await updateClient(initialData.id, {
          ...formData,
          business_name: formData.business_name || initialName || '',
          stato_cliente: isReferenteEdit ? null : (formData.stato_cliente || initialData?.stato_cliente || 'cliente'),
          ...(isReferenteEdit ? { pipeline_id: null, pipeline_stage_id: null } : {}),
        });
      } else {
        // Create new client/lead
        result = await createClient({
          ...formData,
          business_name: formData.business_name || initialName || '',
          stato_cliente: formData.stato_cliente || initialData?.stato_cliente || 'cliente'
        });
      }

      if (result) {
        toast.success(settings?.language === 'it' ?
          (isEditing ? `${clientLabel.singular} modificato con successo` : `${clientLabel.singular} creato con successo`) :
          (isEditing ? 'Client updated successfully' : 'Client created successfully'));
        onClientCreated(result.id);
        onClose();
        return result;
      } else {
        throw new Error(isEditing ? 'Update failed' : 'Create failed');
      }
    } catch (error) {
      toast.error(settings?.language === 'it' ?
        (isEditing ? `Errore durante la modifica ${clientLabel.del}${clientLabel.singularLower}` : `Errore durante la creazione ${clientLabel.del}${clientLabel.singularLower}`) :
        (isEditing ? 'Error updating client' : 'Error creating client'));
      console.error(`Error ${isEditing ? 'updating' : 'creating'} client:`, error);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  // Drawer Notion-like anche qui (chat WhatsApp, badge cliente): il wizard a
  // passi è stato eliminato il 03/09/2026. Stessa shape dati e stesso onComplete.
  return (
    <CreateClientDrawer
      isOpen={isOpen}
      onClose={onClose}
      mode={isEditing ? 'edit' : 'create'}
      initialData={initialData || {
        entity_type: 'company',
        business_name: initialName || '',
        stato_cliente: 'cliente',
      }}
      onComplete={handleSubmit}
      isSubmitting={isSubmitting}
      isLead={isLead}
    />
  );
}
